import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from '@nextui-org/react';
import { startOfISOWeek, getISOWeek, format } from 'date-fns';
import { es } from 'date-fns/locale';
import LineChart from './LineChart';
import ChartDownloadButton from './ChartDownloadButton';
import { useRetailPrices } from '../../hooks/useRetailPrices';
import { useTranslation } from '../../hooks/useTranslation';
import {
  getCountryLabel,
  getRegionForCountry,
  getRegionLabel,
  REGION_ORDER,
  REGION_CHART_COLORS,
} from '../../utils/retail-labels';
import { countPricePointsForSkus } from '../../api/retail_skus';
import { getChartThemeColors } from '../../utils/chartTheme';
import useUserStore from '../../stores/useUserStore';
import { retailPropertyValues, type RetailProperty, type RetailPropertyAssignments } from '../../utils/retail-properties';
import { Alert, EmptyState } from '../ui';

export type RetailGroupBy = 'region' | 'group' | 'country' | 'retailer' | 'sku' | 'property';
export type RetailDisplayMode = 'separado' | 'promedio';

// A SKU must have at least this many TOTAL price records (all-time, same
// count as the "Registros" column in superadmin) to be plotted. Keeps thin,
// noisy SKUs out of the market chart. Relaxed in local dev (where the DB
// only has a couple of records per SKU) so the chart is testable.
const MIN_RECORDS = import.meta.env.DEV ? 1 : 10;

// Fixed series palette, same hexes as the USDA explorer charts so both
// screens share the color coding. First color doubles as the brand-ish
// anchor for the single "Promedio global" series.
const SERIES_COLORS = [
  '#e11d48',
  '#2563eb',
  '#16a34a',
  '#9333ea',
  '#ea580c',
  '#0891b2',
  '#ca8a04',
  '#db2777',
  '#65a30d',
  '#4f46e5',
  '#14b8a6',
  '#f97316',
  '#8b5cf6',
  '#0ea5e9',
  '#84cc16',
  '#f59e0b',
];

// "2026-06-08" (an ISO-week Monday) → "S23 jun" for the x-axis labels.
const weekLabelFromKey = (key: string): string => {
  const m = key.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return key;
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return `S${getISOWeek(dt)} ${format(dt, 'MMM', { locale: es })}`;
};

const escapeHtml = (value: string) => value.replace(/[&<>"']/g, (character) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]!
));

interface Props {
  speciesId: number;
  targetSkuIds?: number[];
  dateRange?: [Date, Date];
  countries?: string[];
  retailers?: string[];
  tiers?: string[];
  groupBy?: RetailGroupBy;
  displayMode?: RetailDisplayMode;
  /** USD/kg (default) or the SKUs' local currency. Controlled from the
   *  filter sidebar; only takes effect when a single currency is on screen. */
  priceMode?: 'usd' | 'local';
  /** Card header title (the active grouping tab label). */
  title?: string;
  /** Filter selects rendered between the header and the chart (evolution
   *  layout: title → filters → chart), always visible even with no data. */
  filtersSlot?: React.ReactNode;
  /** target_sku_id → species_group. Resuelto en el caller (que ya carga los
   *  SKUs para los filtros); retail_prices no denormaliza el grupo, así que
   *  agrupar por grupo siempre refleja la asignación actual del SKU. */
  skuGroups?: Map<number, string | null>;
  /** Solo SKUs de estos grupos (vacío = todos). */
  groups?: string[];
  filtersLoading?: boolean;
  property?: RetailProperty;
  propertyAssignments?: RetailPropertyAssignments;
}

const RetailEvolutionChart: React.FC<Props> = ({
  speciesId,
  targetSkuIds,
  dateRange,
  countries,
  retailers,
  tiers,
  groupBy = 'country',
  displayMode = 'separado',
  priceMode = 'usd',
  title,
  filtersSlot,
  skuGroups,
  groups,
  filtersLoading = false,
  property,
  propertyAssignments,
}) => {
  const { t } = useTranslation('market');
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const { darkMode } = useUserStore();
  const themeColors = getChartThemeColors(darkMode);
  const { data, loading, error } = useRetailPrices({
    speciesId,
    targetSkuIds,
    countries: countries?.length ? countries : undefined,
    retailers: retailers?.length ? retailers : undefined,
    tiers: tiers?.length ? tiers : undefined,
    dateFrom: dateRange?.[0],
    dateTo: dateRange?.[1],
  });

  // All-time record count per SKU (matches the superadmin "Registros" column).
  // SKUs below MIN_RECORDS are dropped from the chart. `null` = not loaded yet.
  const [skuTotalsResult, setSkuTotalsResult] = useState<{
    key: string;
    totals: Map<number, number>;
  } | null>(null);
  const skuIdsKey = useMemo(
    () =>
      Array.from(new Set(data.map((d) => d.target_sku_id)))
        .sort((a, b) => a - b)
        .join(','),
    [data]
  );
  const skuTotals =
    skuTotalsResult?.key === skuIdsKey ? skuTotalsResult.totals : null;
  const totalsLoading = Boolean(skuIdsKey) && skuTotals === null;

  useEffect(() => {
    const ids = skuIdsKey ? skuIdsKey.split(',').map(Number) : [];
    if (!ids.length) {
      setSkuTotalsResult(null);
      return;
    }
    let cancelled = false;
    countPricePointsForSkus(ids)
      .then((m) => {
        if (!cancelled) setSkuTotalsResult({ key: skuIdsKey, totals: m });
      })
      .catch(() => {
        if (!cancelled) {
          setSkuTotalsResult({ key: skuIdsKey, totals: new Map() });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [skuIdsKey]);

  // Local-currency mode is only available when there's a single currency
  // among the plotted SKUs (e.g. one country, or all of the Eurozone in EUR).
  const localCurrency = useMemo(() => {
    const rows = skuTotals
      ? data.filter((d) => (skuTotals.get(d.target_sku_id) ?? 0) >= MIN_RECORDS)
      : data;
    const set = new Set<string>();
    rows.forEach((d) => {
      if (d.price_usd_per_kg != null && d.currency) set.add(d.currency);
    });
    return set.size === 1 ? Array.from(set)[0] : null;
  }, [data, skuTotals]);
  const canLocal = localCurrency != null;
  const effectiveMode: 'usd' | 'local' =
    priceMode === 'local' && canLocal ? 'local' : 'usd';
  const unitCurrency = effectiveMode === 'local' ? localCurrency! : 'USD';
  // Currency-aware price formatter for tooltip / summary.
  const fmtPrice = (v: number): string =>
    effectiveMode === 'local'
      ? `${v.toLocaleString('es', { maximumFractionDigits: 2 })} ${localCurrency}`
      : `$${v.toFixed(2)}`;

  // Group into series. Multiple datapoints in same group/date get averaged.
  const { series, xaxisCategories, recordCount } = useMemo(() => {
    if (!data.length)
      return { series: [], xaxisCategories: [] as string[], recordCount: 0 };

    // Only plot SKUs with >= MIN_RECORDS total records. The loading state
    // keeps the chart hidden until counts for the current SKUs arrive.
    let rows = skuTotals
      ? data.filter((d) => (skuTotals.get(d.target_sku_id) ?? 0) >= MIN_RECORDS)
      : data;
    // El grupo vive en retail_target_skus, no en retail_prices: se filtra acá
    // con el mapa que arma el caller.
    if (groups?.length && skuGroups) {
      const wanted = new Set(groups);
      rows = rows.filter((d) => {
        const g = skuGroups.get(d.target_sku_id);
        return g != null && wanted.has(g);
      });
    }
    if (groupBy === 'property') {
      rows = property ? rows.filter((d) => retailPropertyValues(d.target_sku_id, property, propertyAssignments).length > 0) : [];
    }
    if (!rows.length)
      return { series: [], xaxisCategories: [] as string[], recordCount: 0 };

    const computeKey = (d: typeof data[number]): string => {
      // Promedio collapses everything into a single line
      if (displayMode === 'promedio') return t('Promedio global');
      switch (groupBy) {
        case 'group':
          return skuGroups?.get(d.target_sku_id) || t('Sin grupo');
        case 'region':
          return getRegionLabel(t, getRegionForCountry(d.country));
        case 'country':
          return getCountryLabel(d.country);
        case 'retailer':
          return d.retailer;
        case 'sku':
        default: {
          // Use product_name + package_size_text to disambiguate multiple SKUs
          // from the same retailer in the same country (e.g., AH NL has 2 SKUs).
          const name = (d.product_name ?? '').slice(0, 40);
          const size = d.package_size_text ? ` (${d.package_size_text})` : '';
          return `${d.retailer} ${d.country} · ${name}${size}`;
        }
      }
    };

    const allDates = new Set<string>();
    // group key -> date -> [prices to average]
    const buckets = new Map<string, Map<string, number[]>>();

    // captured_at is UTC in DB; convert to LOCAL date and bucket by ISO week
    // (Monday) so the timeline groups daily scrapes into one weekly point —
    // the key is the Monday's date (YYYY-MM-DD), rendered as "Sxx mes".
    const toWeekKey = (utcIso: string): string => {
      const dt = new Date(utcIso);
      const localDay = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
      const monday = startOfISOWeek(localDay);
      const yyyy = monday.getFullYear();
      const mm = String(monday.getMonth() + 1).padStart(2, '0');
      const dd = String(monday.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    // USD/kg by default; in local mode convert back with the row's FX rate
    // (price_usd_per_kg * fx_rate_used = original local/kg at scrape time).
    const priceOf = (d: typeof data[number]): number | null => {
      if (effectiveMode === 'local') {
        return d.price_usd_per_kg != null && d.fx_rate_used != null
          ? +(d.price_usd_per_kg * d.fx_rate_used).toFixed(2)
          : null;
      }
      return d.price_usd_per_kg;
    };

    rows.forEach((d) => {
      const y = priceOf(d);
      if (y == null) return;
      const dateStr = toWeekKey(d.captured_at);
      allDates.add(dateStr);
      let keys = [computeKey(d)];
      if (groupBy === 'property' && property && displayMode !== 'promedio') {
        const values = retailPropertyValues(d.target_sku_id, property, propertyAssignments);
        keys = values.map((value) => `${property.name}: ${value}`);
      }
      // Multiple values contribute to each matching series. In global-average
      // mode the original record contributes only once.
      keys.forEach((key) => {
        if (!buckets.has(key)) buckets.set(key, new Map());
        const inner = buckets.get(key)!;
        if (!inner.has(dateStr)) inner.set(dateStr, []);
        inner.get(dateStr)!.push(y);
      });
    });

    const dates = Array.from(allDates).sort();
    let entries = Array.from(buckets.entries());
    if (groupBy === 'region' && displayMode !== 'promedio') {
      const order = REGION_ORDER.map((r) => getRegionLabel(t, r));
      entries = entries.sort(
        (a, b) => order.indexOf(a[0]) - order.indexOf(b[0])
      );
    }
    if (groupBy === 'property' && property && displayMode !== 'promedio') {
      const order = property.options.map((value) => `${property.name}: ${value}`);
      entries.sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]));
    }
    const seriesArr = entries.map(([name, dateMap]) => ({
      name,
      data: dates.map((dateStr) => {
        const prices = dateMap.get(dateStr) ?? [];
        const avg = prices.length
          ? +(prices.reduce((a, b) => a + b, 0) / prices.length).toFixed(3)
          : null;
        return { x: dateStr, y: avg };
      }),
    }));

    return {
      series: seriesArr,
      xaxisCategories: dates,
      recordCount: rows.length,
    };
  }, [data, skuTotals, groupBy, displayMode, effectiveMode, skuGroups, groups, property, propertyAssignments, t]);

  // Region series get their fixed color; the rest go by index.
  const regionColorByLabel = useMemo(
    () =>
      new Map(
        REGION_ORDER.map((r) => [getRegionLabel(t, r), REGION_CHART_COLORS[r]])
      ),
    [t]
  );
  const seriesColors = useMemo(
    () =>
      series.map(
        (s, i) =>
          (groupBy === 'region' && regionColorByLabel.get(s.name || '')) ||
          SERIES_COLORS[i % SERIES_COLORS.length]
      ),
    [series, groupBy, regionColorByLabel]
  );


  // Header + filters stay mounted through loading/error/empty states so the
  // user can always adjust filters (evolution layout).
  const body = loading || totalsLoading || filtersLoading ? (
    <div className='flex justify-center py-12'>
      <Spinner size='md' label={t('Cargando precios retail...')} />
    </div>
  ) : error ? (
    <Alert variant='danger' showIcon>
      Error: {error}
    </Alert>
  ) : !data.length ? (
    <EmptyState title={t('Sin datos retail para los filtros actuales.')} />
  ) : null;

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col sm:flex-row justify-between items-center'>
        <div className='flex items-center gap-2'>
          <h3 className='text-lg font-bold'>{title ?? t('Retail')}</h3>
          {!body && series.length > 0 && (
            <ChartDownloadButton
              chartRef={chartContainerRef}
              title={title ?? t('Retail')}
              subtitle={`${recordCount} ${t('registros totales')}`}
            />
          )}
        </div>
        <p className='text-sm text-default-500'>
          {recordCount} {t('registros totales')}
        </p>
      </div>

      {filtersSlot}

      {body}

      {!body && (
      <div ref={chartContainerRef}>

      {series.length === 0 ? (
        <p className='text-default-500 text-center py-6'>
          {t(
            groupBy === 'property'
              ? 'No hay precios disponibles para SKUs con esta propiedad asignada y los filtros actuales.'
              : 'Ningún SKU alcanza el mínimo de 10 registros todavía, o no hay precios convertibles a USD/kg con los filtros actuales.'
          )}
        </p>
      ) : (
        <LineChart
          series={series}
          xaxisCategories={xaxisCategories}
          height={420}
          yaxisTitle={`Precio retail (${unitCurrency}/kg)`}
          selectedCurrency={unitCurrency}
          colors={seriesColors}
          priceView='kg'
          viewMode='week'
          xLabelFormatter={weekLabelFromKey}
          yLabelFormatter={
            effectiveMode === 'local'
              ? (v) =>
                  `${v.toLocaleString('es', {
                    maximumFractionDigits: v < 100 ? 1 : 0,
                  })} ${localCurrency}`
              : undefined
          }
          legendPosition='bottom'
          tooltipCustom={({ dataPointIndex, w }) => {
            // Take all series values at this date, drop empties, sort desc
            const items = (w.globals.seriesNames as string[])
              .map((name, i) => ({
                name,
                value: w.globals.series[i]?.[dataPointIndex] as number | null,
                color: w.globals.colors[i] as string,
              }))
              .filter(
                (it) =>
                  it.value != null &&
                  !Number.isNaN(it.value) &&
                  Number(it.value) > 0
              )
              .sort((a, b) => Number(b.value) - Number(a.value));

            if (items.length === 0) return '';

            const dateRaw = xaxisCategories[dataPointIndex] ?? '';
            let dateLabel = String(dateRaw);
            if (
              typeof dateRaw === 'string' &&
              dateRaw.match(/^\d{4}-\d{2}-\d{2}$/)
            ) {
              const [year, month, day] = dateRaw.split('-').map(Number);
              const localDate = new Date(year, month - 1, day);
              dateLabel = `Semana ${getISOWeek(localDate)} · ${format(
                localDate,
                'MMM yyyy',
                { locale: es }
              )}`;
            }

            const rows = items
              .map(
                (it) => `
                  <div style="display:flex; align-items:center; gap:8px; padding:3px 10px; font-size:12px;">
                    <span style="width:8px; height:8px; border-radius:50%; background:${it.color}; flex-shrink:0;"></span>
                    <span style="flex:1; min-width:0; max-width:220px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; color:${themeColors.tooltipText};" title="${escapeHtml(it.name)}">${escapeHtml(it.name)}</span>
                    <span style="font-weight:600; color:${themeColors.tooltipValue}; font-variant-numeric:tabular-nums;">${fmtPrice(Number(it.value))} /kg</span>
                  </div>
                `
              )
              .join('');

            return `
              <div style="background:${themeColors.tooltipBg}; border:1px solid ${themeColors.tooltipBorder}; border-radius:6px; box-shadow:${themeColors.tooltipShadow}; min-width:240px;">
                <div style="padding:6px 10px; border-bottom:1px solid ${themeColors.tooltipBorder}; font-weight:600; font-size:12px; color:${themeColors.tooltipTitle};">
                  ${dateLabel}
                </div>
                <div style="padding:4px 0;">
                  ${rows}
                </div>
              </div>
            `;
          }}
        />
      )}
      </div>
      )}

      <p className='text-xs text-default-400'>
        {t('Precio convertido a USD/kg al momento del scrape, usando tasa ECB del día (Frankfurter).')}
      </p>
    </div>
  );
};

export default RetailEvolutionChart;
